let mockUsername: string | null = 'chesslearner';
import type {
  DeviationEvent,
  DeviationRun,
  EdgePatch,
  Game,
  Job,
  LineCreate,
  MoveAdd,
  NodeRef,
  PositionNode,
  Problem,
  Repertoire,
  RepertoireCreate,
  RepertoireEdge,
  RepertoireLine,
  Transition,
} from '../api/types';
import { legalUciMoves, playUci } from './chess';
import { mockDetect, mockMatch, type MockRepertoireGraph } from './game-analysis';
import {
  mockGradeReview,
  mockReviewQueue,
  mockReviewSummary,
  seedMockReviews,
  type MockReviewGraph,
} from './review';
import {
  edgeByKey,
  lineA,
  lineB,
  lines,
  mockEvaluation,
  mockExplorerMoves,
  mockGames,
  mockGamesById,
  mockJob,
  mockJobEvents,
  mockProviderAccounts,
  nodeInfo,
  outgoingFor,
  positions,
  repertoire,
} from './fixtures';

/**
 * A §4 mock server implemented as a fetch interceptor. It serves the generated
 * client for local development and tests without a backend process, and it is
 * built strictly from the §4 shapes — no invented endpoints.
 */

let repertoireVersion = Number(repertoire.version);

// Mutable repertoire store so create/delete are observable in the mock.
let mockRepertoireList: Repertoire[] = [repertoire];
let mockRepertoireCounter = 700;
let mockRootCounter = 750;
const mockRepertoireCreates = new Map<string, Repertoire>();
const mockEdgesByRepertoire = new Map<string, RepertoireEdge[]>([
  [repertoire.repertoireId, [...edgeByKey.values()]],
]);
let mockEdgeCounter = 1000;
let mockPositionCounter = 2000;
let mockTransitionCounter = 2000;

function positionKey(fen: string): string {
  return fen.trim().split(/\s+/).slice(0, 4).join(' ');
}

const mockPositionsById = new Map(positions.map((position) => [position.positionId, position]));
const mockPositionsByState = new Map(
  positions.map((position) => [positionKey(position.canonicalFen), position]),
);
const fixturePositionIds = new Set(positions.map((position) => position.positionId));

function ensureMockPosition(fen: string): PositionNode {
  const key = positionKey(fen);
  const existing = mockPositionsByState.get(key);
  if (existing) return existing;
  mockPositionCounter += 1;
  const fields = fen.trim().split(/\s+/);
  const enPassant = fields[3] ?? '-';
  const created: PositionNode = {
    hashVersion: 1,
    zobristHash: mockPositionCounter.toString(16).padStart(32, '0'),
    positionId: String(mockPositionCounter),
    canonicalFen: fen,
    sideToMove: fields[1] === 'b' ? 'b' : 'w',
    castling: fields[2] || '-',
    enPassantFile: enPassant === '-' ? null : enPassant.charCodeAt(0) - 'a'.charCodeAt(0),
  };
  mockPositionsById.set(created.positionId, created);
  mockPositionsByState.set(key, created);
  return created;
}

function asNodeRef(position: PositionNode): NodeRef {
  return {
    hashVersion: position.hashVersion,
    zobristHash: position.zobristHash,
    positionId: position.positionId,
  };
}

function nextMockTransitionId(): string {
  mockTransitionCounter += 1;
  return String(mockTransitionCounter);
}

function nextMockRepertoireId(): string {
  mockRepertoireCounter += 1;
  return `00000000-0000-4000-8000-${mockRepertoireCounter.toString(16).padStart(12, '0')}`;
}

function nextMockRootId(): string {
  mockRootCounter += 1;
  return `00000000-0000-4000-8000-${mockRootCounter.toString(16).padStart(12, '0')}`;
}

function nextMockEdgeId(): string {
  mockEdgeCounter += 1;
  return `00000000-0000-4000-8000-${mockEdgeCounter.toString(16).padStart(12, '0')}`;
}

// Mutable line store so createLine/deleteLine are observable in the mock.
let mockLineList: RepertoireLine[] = [lineA, lineB];
let mockLineCounter = 900;
function nextMockLineId(): string {
  mockLineCounter += 1;
  return `00000000-0000-4000-8000-${mockLineCounter.toString(16).padStart(12, '0')}`;
}

// Mutable linked-account store so link/unlink are observable in the mock.
let mockAccountList = [...mockProviderAccounts];
let mockAccountCounter = 800;
function nextMockAccountId(): string {
  mockAccountCounter += 1;
  return `00000000-0000-4000-8000-${mockAccountCounter.toString(16).padStart(12, '0')}`;
}

const MOCK_STORAGE_KEY = 'chessreps:mock-api:v2';

interface PersistedMockState {
  repertoireVersion: number;
  repertoireCounter: number;
  rootCounter: number;
  edgeCounter: number;
  positionCounter: number;
  transitionCounter: number;
  lineCounter: number;
  repertoires: Repertoire[];
  lines: RepertoireLine[];
  edges: Array<[string, RepertoireEdge[]]>;
  positions: PositionNode[];
}

function browserStorage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
}

function persistMockState(): void {
  const storage = browserStorage();
  if (!storage) return;
  const state: PersistedMockState = {
    repertoireVersion,
    repertoireCounter: mockRepertoireCounter,
    rootCounter: mockRootCounter,
    edgeCounter: mockEdgeCounter,
    positionCounter: mockPositionCounter,
    transitionCounter: mockTransitionCounter,
    lineCounter: mockLineCounter,
    repertoires: mockRepertoireList,
    lines: mockLineList,
    edges: [...mockEdgesByRepertoire.entries()],
    positions: [...mockPositionsById.values()],
  };
  try {
    storage.setItem(MOCK_STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Mock mode still works in-memory when storage is unavailable or full.
  }
}

function hydrateMockState(): void {
  const storage = browserStorage();
  if (!storage) return;
  try {
    const raw = storage.getItem(MOCK_STORAGE_KEY);
    if (!raw) return;
    const state = JSON.parse(raw) as PersistedMockState;
    if (!Array.isArray(state.repertoires) || !Array.isArray(state.lines)) return;
    repertoireVersion = state.repertoireVersion;
    mockRepertoireCounter = state.repertoireCounter;
    mockRootCounter = state.rootCounter;
    mockEdgeCounter = state.edgeCounter;
    mockPositionCounter = state.positionCounter;
    mockTransitionCounter = state.transitionCounter;
    mockLineCounter = state.lineCounter;
    mockRepertoireList = state.repertoires;
    mockLineList = state.lines;
    mockEdgesByRepertoire.clear();
    for (const [repertoireId, repertoireEdges] of state.edges) {
      mockEdgesByRepertoire.set(repertoireId, repertoireEdges);
    }
    mockPositionsById.clear();
    mockPositionsByState.clear();
    for (const storedPosition of state.positions) {
      mockPositionsById.set(storedPosition.positionId, storedPosition);
      mockPositionsByState.set(positionKey(storedPosition.canonicalFen), storedPosition);
    }
  } catch {
    storage.removeItem(MOCK_STORAGE_KEY);
  }
}

hydrateMockState();

// Stateful mock durable job: POST /imports starts a fresh queued job; each
// GET /jobs/{id} advances it one step so polling reaches a terminal state.
let currentJob: Job | null = null;
let jobPollCount = 0;

function startImportJob(): Job {
  jobPollCount = 0;
  currentJob = {
    ...mockJob,
    status: 'queued',
    progress: { completed: '0', total: '3' },
    completedAt: null,
  };
  return currentJob;
}

function startProviderSyncJob(): Job {
  const job = startImportJob();
  currentJob = {
    ...job,
    result: { games_imported: 3, games_already_present: 0, games_skipped: 0 },
  };
  return currentJob;
}

function advanceJob(): Job {
  if (currentJob === null) return mockJob;
  if (currentJob.status === 'succeeded' || currentJob.status === 'failed') return currentJob;
  jobPollCount += 1;
  if (jobPollCount >= 3) {
    currentJob = {
      ...currentJob,
      status: 'succeeded',
      progress: { completed: '3', total: '3' },
      completedAt: '2025-01-03T00:00:03Z',
    };
  } else {
    currentJob = {
      ...currentJob,
      status: 'running',
      progress: { completed: String(jobPollCount), total: '3' },
      startedAt: '2025-01-03T00:00:01Z',
    };
  }
  return currentJob;
}

function startDeviationJob(runId: string): Job {
  jobPollCount = 0;
  currentJob = {
    ...mockJob,
    kind: 'deviation_detection',
    status: 'queued',
    progress: { completed: '0', total: '3' },
    result: { runId },
    completedAt: null,
  };
  return currentJob;
}

function problem(status: number, code: string, title: string, detail: string): Problem {
  return {
    type: `https://chessreps.example/problems/${code.toLowerCase()}`,
    title,
    status,
    code,
    detail,
    instance: '',
    requestId: '00000000-0000-4000-8000-000000000001',
  };
}

function jsonResponse(body: unknown, init: { status?: number; etag?: string } = {}): Response {
  const headers = new Headers({ 'Content-Type': 'application/json' });
  if (init.etag) headers.set('ETag', init.etag);
  return new Response(JSON.stringify(body), { status: init.status ?? 200, headers });
}

function problemResponse(status: number, code: string, title: string, detail: string): Response {
  const headers = new Headers({ 'Content-Type': 'application/problem+json' });
  return new Response(JSON.stringify(problem(status, code, title, detail)), { status, headers });
}

function currentEtag(): string {
  return `"rep-${repertoireVersion}"`;
}

function repertoireEtag(candidate: Repertoire): string {
  return `"rep-${candidate.version}"`;
}

function currentRepertoireEtag(repertoireId: string): string | null {
  if (repertoireId === repertoire.repertoireId) return currentEtag();
  const target = mockRepertoireList.find((candidate) => candidate.repertoireId === repertoireId);
  return target ? repertoireEtag(target) : null;
}

function bumpRepertoireVersion(repertoireId: string): string {
  if (repertoireId === repertoire.repertoireId) {
    repertoireVersion += 1;
    mockRepertoireList = mockRepertoireList.map((candidate) =>
      candidate.repertoireId === repertoireId
        ? {
            ...candidate,
            version: String(repertoireVersion),
            updatedAt: new Date().toISOString(),
          }
        : candidate,
    );
    persistMockState();
    return currentEtag();
  }
  const target = mockRepertoireList.find((candidate) => candidate.repertoireId === repertoireId);
  if (!target) return '"rep-1"';
  const updated = {
    ...target,
    version: String(Number(target.version) + 1),
    updatedAt: new Date().toISOString(),
  };
  mockRepertoireList = mockRepertoireList.map((candidate) =>
    candidate.repertoireId === repertoireId ? updated : candidate,
  );
  for (const [key, created] of mockRepertoireCreates) {
    if (created.repertoireId === repertoireId) mockRepertoireCreates.set(key, updated);
  }
  persistMockState();
  return repertoireEtag(updated);
}

const sseCache = new Map<string, { body: ReadableStream<Uint8Array> }>();

function sseStream(jobId: string): Response {
  let cached = sseCache.get(jobId);
  if (!cached) {
    const encoder = new TextEncoder();
    let index = 0;
    const body = new ReadableStream<Uint8Array>({
      start(controller) {
        const tick = () => {
          if (index < mockJobEvents.length) {
            const event = mockJobEvents[index++];
            controller.enqueue(
              encoder.encode(
                `id: ${event.id}\nevent: ${event.event}\ndata: ${JSON.stringify(event.data)}\n\n`,
              ),
            );
            setTimeout(tick, 120);
          }
        };
        tick();
      },
    });
    cached = { body };
    sseCache.set(jobId, cached);
  }
  return new Response(cached.body, {
    headers: { 'Content-Type': 'text/event-stream', 'Cache-Control': 'no-cache' },
  });
}

function listLines(repertoireId: string, containsHash: string | null): Response {
  const repertoireLines = mockLineList.filter((line) => line.repertoireId === repertoireId);
  const items = containsHash
    ? repertoireLines.filter((line) => line.nodes.some((n) => n.zobristHash === containsHash))
    : repertoireLines;
  return jsonResponse({ items, nextCursor: null });
}

/** Reconstruct a saved line from this repertoire's private overlay. */
function buildMockLine(repertoireId: string, body: LineCreate): RepertoireLine | null {
  const target = mockRepertoireList.find((candidate) => candidate.repertoireId === repertoireId);
  if (!target || target.defaultRoot.rootId !== body.rootId) return null;
  const root = target.defaultRoot;
  const rootRef: NodeRef = root.position;
  const repertoireEdges = mockEdgesByRepertoire.get(repertoireId) ?? [];
  const nodes: NodeRef[] = [rootRef];
  const movesSan: string[] = [];
  const edgeIds: string[] = [];
  let current = rootRef;
  for (const uci of body.movesUci) {
    const edge = repertoireEdges.find(
      (candidate) =>
        candidate.enabled &&
        candidate.transition.from.positionId === current.positionId &&
        candidate.transition.from.zobristHash === current.zobristHash &&
        candidate.transition.uci === uci,
    );
    if (!edge) return null;
    movesSan.push(edge.transition.san);
    edgeIds.push(edge.edgeId);
    current = edge.transition.to;
    nodes.push(current);
  }
  return {
    lineId: nextMockLineId(),
    repertoireId,
    rootId: root.rootId,
    name: body.name ?? null,
    movesUci: body.movesUci,
    movesSan,
    nodes,
    edgeIds,
    active: true,
    createdAt: new Date().toISOString(),
  };
}

async function updateEdge(
  repertoireId: string,
  edgeId: string,
  ifMatch: string | null,
  rawBody: string,
): Promise<Response> {
  const edge = mockEdgesByRepertoire
    .get(repertoireId)
    ?.find((candidate) => candidate.edgeId === edgeId);
  if (!edge) return problemResponse(404, 'RESOURCE_NOT_FOUND', 'Not found', 'Edge not found');
  if (ifMatch !== currentRepertoireEtag(repertoireId)) {
    return problemResponse(
      412,
      'VERSION_MISMATCH',
      'Stale write',
      'If-Match does not match the current repertoire version.',
    );
  }
  const patch = JSON.parse(rawBody) as EdgePatch;
  if (patch.role !== undefined) edge.role = patch.role;
  if (patch.priority !== undefined) edge.priority = patch.priority;
  if (patch.enabled !== undefined) edge.enabled = patch.enabled;
  if (patch.comment !== undefined) edge.comment = patch.comment;
  if (patch.maxLossCp !== undefined) edge.maxLossCp = patch.maxLossCp;
  return jsonResponse(edge, { etag: bumpRepertoireVersion(repertoireId) });
}

/**
 * v1.1 mock route for POST /repertoires/{repertoireId}/moves. This mock-mode
 * server validates the UCI move, registers the resulting position, and mirrors
 * the real service's get-or-create semantics:
 * identical policy replays the existing edge (201), a different policy on the
 * same source/UCI conflicts (409), and an illegal move is rejected (422).
 */
async function addMockMove(
  repertoireId: string,
  ifMatch: string | null,
  rawBody: string,
): Promise<Response> {
  const expectedEtag = currentRepertoireEtag(repertoireId);
  if (expectedEtag === null) {
    return problemResponse(404, 'RESOURCE_NOT_FOUND', 'Not found', 'Repertoire not found');
  }
  if (ifMatch !== expectedEtag) {
    return problemResponse(
      412,
      'VERSION_MISMATCH',
      'Stale write',
      'If-Match does not match the current repertoire version.',
    );
  }
  let body: MoveAdd;
  try {
    body = JSON.parse(rawBody) as MoveAdd;
  } catch {
    return problemResponse(
      400,
      'BAD_REQUEST',
      'Malformed request',
      'Request body is not valid JSON.',
    );
  }
  const source = mockPositionsByState.get(positionKey(body.fromFen));
  if (!source) {
    return problemResponse(
      422,
      'INVALID_FEN',
      'Invalid FEN position',
      'Source position is not part of the mock repertoire dataset.',
    );
  }
  const played = playUci(source.canonicalFen, body.uci);
  if (!played) {
    return problemResponse(422, 'ILLEGAL_MOVE', 'Illegal move', 'Move is not legal from this position.');
  }
  const repertoireEdges = mockEdgesByRepertoire.get(repertoireId) ?? [];
  const existing = repertoireEdges.find(
    (e) => e.transition.from.positionId === source.positionId && e.transition.uci === body.uci,
  );
  const knownTransition = [...edgeByKey.values()].find(
    (edge) =>
      edge.transition.from.positionId === source.positionId && edge.transition.uci === body.uci,
  )?.transition;
  const target = knownTransition
    ? mockPositionsById.get(knownTransition.to.positionId)!
    : ensureMockPosition(played.fen);
  const transition: Transition =
    knownTransition ?? {
      transitionId: nextMockTransitionId(),
      uci: body.uci,
      san: played.san,
      from: asNodeRef(source),
      to: asNodeRef(target),
    };
  const role = body.role ?? 'alternative';
  const priority = body.priority ?? 100;
  const comment = body.comment === undefined ? null : body.comment;
  const maxLossCp = body.maxLossCp === undefined ? null : body.maxLossCp;
  if (
    existing &&
    (existing.role !== role ||
      existing.priority !== priority ||
      existing.comment !== comment ||
      existing.maxLossCp !== maxLossCp)
  ) {
    return problemResponse(
      409,
      'HASH_COLLISION_AMBIGUOUS',
      'Hash collision is ambiguous',
      'Existing edge policy differs; PATCH it explicitly.',
    );
  }
  if (existing) return jsonResponse(existing, { status: 201, etag: expectedEtag });

  const created: RepertoireEdge = {
    edgeId: nextMockEdgeId(),
    transition,
    role,
    priority,
    enabled: true,
    comment,
    maxLossCp,
  };
  mockEdgesByRepertoire.set(repertoireId, [...repertoireEdges, created]);
  return jsonResponse(created, {
    status: 201,
    etag: bumpRepertoireVersion(repertoireId),
  });
}

function repertoireGraphs(): MockRepertoireGraph[] {
  return mockRepertoireList.map((candidate) => ({
    repertoire: candidate,
    edges: mockEdgesByRepertoire.get(candidate.repertoireId) ?? [],
  }));
}

const analysisContext = {
  positionByFen: (fen: string) => mockPositionsByState.get(positionKey(fen)),
};

// Deviation runs computed on demand against whatever repertoire was chosen.
const mockRuns = new Map<string, { run: DeviationRun; events: DeviationEvent[] }>();
let mockRunCounter = 1100;

function createMockRun(gameId: string, repertoireId: string): string | null {
  const game = mockGamesById.get(gameId);
  const graph = repertoireGraphs().find((candidate) => candidate.repertoire.repertoireId === repertoireId);
  if (!game || !graph) return null;
  mockRunCounter += 1;
  const runId = `00000000-0000-4000-8000-${mockRunCounter.toString(16).padStart(12, '0')}`;
  const events = mockDetect(game, graph, analysisContext);
  mockRuns.set(runId, {
    run: {
      runId, gameId, repertoireId, algorithmVersion: 3, status: 'succeeded', eventCount: events.length,
      createdAt: new Date().toISOString(), completedAt: new Date().toISOString(),
    },
    events,
  });
  return runId;
}

function reviewGraph(repertoireId: string): MockReviewGraph | null {
  const target = mockRepertoireList.find((candidate) => candidate.repertoireId === repertoireId);
  if (!target) return null;
  return {
    side: target.side,
    rootPositionId: target.defaultRoot.position.positionId,
    edges: mockEdgesByRepertoire.get(repertoireId) ?? [],
    position: (positionId) => mockPositionsById.get(positionId),
  };
}

function reviewScope(url: URL, graph: MockReviewGraph): string | undefined | null {
  const hash = url.searchParams.get('fromHash');
  const positionId = url.searchParams.get('fromPositionId');
  if (hash === null && positionId === null) return undefined;
  const position = positionId === null ? undefined : graph.position(positionId);
  return position && position.zobristHash === hash ? position.positionId : null;
}

async function handleReview(
  request: Request,
  url: URL,
  repertoireId: string,
  resource: string,
): Promise<Response> {
  const graph = reviewGraph(repertoireId);
  if (!graph) return problemResponse(404, 'RESOURCE_NOT_FOUND', 'Not found', 'Repertoire not found');
  if (repertoireId === repertoire.repertoireId) seedMockReviews(repertoireId, graph);
  const method = request.method.toUpperCase();
  if (method === 'POST' && resource === '') {
    if (!request.headers.get('Idempotency-Key')) {
      return problemResponse(428, 'PRECONDITION_REQUIRED', 'Precondition header required', 'Idempotency-Key is required.');
    }
    const result = mockGradeReview(repertoireId, graph, JSON.parse(await request.text()));
    if (result === 'not-found') return problemResponse(404, 'RESOURCE_NOT_FOUND', 'Not found', 'Edge not found');
    if (result === 'not-scheduled') {
      return problemResponse(409, 'SRS_ITEM_NOT_SCHEDULED', 'The edge is not a review item', 'Only your own moves are reviewed.');
    }
    return jsonResponse(result, { status: 201 });
  }
  const scope = reviewScope(url, graph);
  if (scope === null) {
    return problemResponse(404, 'RESOURCE_NOT_FOUND', 'Not found', 'The scope position is not in this repertoire');
  }
  if (method === 'GET' && resource === '/summary') {
    return jsonResponse(mockReviewSummary(repertoireId, graph, scope));
  }
  if (method === 'GET' && resource === '/queue') {
    const mode = (url.searchParams.get('mode') ?? 'due') as 'due' | 'new' | 'all';
    const limit = Number(url.searchParams.get('limit') ?? '20');
    return jsonResponse(mockReviewQueue(repertoireId, graph, mode, limit, scope));
  }
  return problemResponse(404, 'RESOURCE_NOT_FOUND', 'Not found', 'No mock review route');
}

export async function mockFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  // openapi-fetch calls fetch with a Request object; normalize both forms.
  const request = input instanceof Request ? input : new Request(String(input), init);
  const url = new URL(request.url);
  const method = request.method.toUpperCase();
  const pathname = url.pathname.replace(/^\/v1/, '') || '/';
  const ifMatch = request.headers.get('If-Match');

  const repertoireMatch = /^\/repertoires\/([^/]+)$/.exec(pathname);
  const linesMatch = /^\/repertoires\/([^/]+)\/lines$/.exec(pathname);
  const lineMatch = /^\/repertoires\/([^/]+)\/lines\/([^/]+)$/.exec(pathname);
  const nodeMatch = /^\/repertoires\/([^/]+)\/nodes\/([0-9a-f]{32})\/([0-9]+)$/.exec(pathname);
  const edgeMatch = /^\/repertoires\/([^/]+)\/edges\/([^/]+)$/.exec(pathname);
  const movesMatch = /^\/repertoires\/([^/]+)\/moves$/.exec(pathname);
  const reviewMatch = /^\/repertoires\/([^/]+)\/review(\/summary|\/queue)?$/.exec(pathname);
  const jobMatch = /^\/jobs\/([^/]+)$/.exec(pathname);
  const jobEventsMatch = /^\/jobs\/([^/]+)\/events$/.exec(pathname);
  const evaluationMatch = /^\/positions\/([0-9a-f]{32})\/([0-9]+)\/evaluations\/([^/]+)$/.exec(
    pathname,
  );
  const deviationRunCreateMatch = /^\/games\/([^/]+)\/deviation-runs$/.exec(pathname);
  const gameMatch = /^\/games\/([^/]+)$/.exec(pathname);
  const deviationRunMatch = /^\/deviation-runs\/([^/]+)$/.exec(pathname);
  const deviationEventsMatch = /^\/deviation-runs\/([^/]+)\/events$/.exec(pathname);
  const providerAccountMatch = /^\/provider-accounts\/([^/]+)$/.exec(pathname);
  const providerSyncMatch = /^\/provider-accounts\/([^/]+)\/syncs$/.exec(pathname);

  if (pathname === '/account/profile' && method === 'GET')
    return jsonResponse({ username: mockUsername });
  if (pathname === '/account/profile' && method === 'PUT') {
    const username = (JSON.parse(await request.text()).username as string).trim().toLowerCase();
    if (!/^[a-z][a-z0-9_]{2,23}$/.test(username))
      return problemResponse(400, 'BAD_REQUEST', 'Invalid username', 'Choose a valid username.');
    mockUsername = username;
    return jsonResponse({ username });
  }
  if (method === 'GET' && pathname === '/repertoires') {
    return jsonResponse({ items: mockRepertoireList, nextCursor: null });
  }
  if (method === 'POST' && pathname === '/repertoires') {
    const rawBody = await request.text();
    let body: RepertoireCreate;
    try {
      body = JSON.parse(rawBody) as RepertoireCreate;
    } catch {
      return problemResponse(400, 'BAD_REQUEST', 'Malformed request', 'Request body is not valid JSON.');
    }
    const idempotencyKey = request.headers.get('Idempotency-Key') ?? '';
    const workspaceId = request.headers.get('X-Workspace-ID') ?? repertoire.workspaceId;
    const replay = mockRepertoireCreates.get(`${workspaceId}:${idempotencyKey}`);
    if (replay) {
      return jsonResponse(replay, { status: 201, etag: repertoireEtag(replay) });
    }
    const now = new Date().toISOString();
    const created: Repertoire = {
      repertoireId: nextMockRepertoireId(),
      workspaceId,
      name: body.name,
      side: body.side,
      visibility: body.visibility ?? 'private',
      version: '1',
      defaultRoot: {
        rootId: nextMockRootId(),
        name: 'Start position',
        position: repertoire.defaultRoot.position,
        default: true,
      },
      settings: body.settings ?? {},
      createdAt: now,
      updatedAt: now,
    };
    mockRepertoireList = [...mockRepertoireList, created];
    mockRepertoireCreates.set(`${workspaceId}:${idempotencyKey}`, created);
    mockEdgesByRepertoire.set(created.repertoireId, []);
    persistMockState();
    return jsonResponse(created, { status: 201, etag: repertoireEtag(created) });
  }
  if (method === 'GET' && repertoireMatch) {
    const found = mockRepertoireList.find(
      (candidate) => candidate.repertoireId === repertoireMatch[1],
    );
    if (!found) return problemResponse(404, 'RESOURCE_NOT_FOUND', 'Not found', 'Repertoire not found');
    return jsonResponse(found, {
      etag: found.repertoireId === repertoire.repertoireId ? currentEtag() : repertoireEtag(found),
    });
  }
  if (method === 'PATCH' && repertoireMatch) {
    const found = mockRepertoireList.find((candidate) => candidate.repertoireId === repertoireMatch[1]);
    if (!found) return problemResponse(404, 'RESOURCE_NOT_FOUND', 'Not found', 'Repertoire not found');
    const body = JSON.parse(await request.text()) as { name?: string };
    const updated = { ...found, name: body.name?.trim() || found.name, updatedAt: new Date().toISOString(), version: String(Number(found.version) + 1) };
    mockRepertoireList = mockRepertoireList.map((candidate) => candidate.repertoireId === updated.repertoireId ? updated : candidate);
    persistMockState();
    return jsonResponse(updated, { etag: repertoireEtag(updated) });
  }
  if (method === 'DELETE' && repertoireMatch) {
    mockRepertoireList = mockRepertoireList.filter((r) => r.repertoireId !== repertoireMatch[1]);
    mockEdgesByRepertoire.delete(repertoireMatch[1]);
    mockLineList = mockLineList.filter((line) => line.repertoireId !== repertoireMatch[1]);
    persistMockState();
    return new Response(null, { status: 204 });
  }
  if (method === 'GET' && linesMatch) {
    return listLines(linesMatch[1], url.searchParams.get('containsHash'));
  }
  if (method === 'GET' && lineMatch) {
    const line = mockLineList.find(
      (candidate) =>
        candidate.repertoireId === lineMatch[1] && candidate.lineId === lineMatch[2],
    );
    if (!line) return problemResponse(404, 'RESOURCE_NOT_FOUND', 'Not found', 'Line not found');
    return jsonResponse(line);
  }
  if (method === 'PATCH' && lineMatch) {
    const [repertoireId, lineId] = [lineMatch[1], lineMatch[2]];
    const expectedEtag = currentRepertoireEtag(repertoireId);
    if (expectedEtag === null) {
      return problemResponse(404, 'RESOURCE_NOT_FOUND', 'Not found', 'Repertoire not found');
    }
    const found = mockLineList.find(
      (candidate) => candidate.repertoireId === repertoireId && candidate.lineId === lineId,
    );
    if (!found) return problemResponse(404, 'RESOURCE_NOT_FOUND', 'Not found', 'Line not found');
    if (ifMatch !== expectedEtag) {
      return problemResponse(412, 'VERSION_MISMATCH', 'Stale write', 'Repertoire changed.');
    }
    const body = JSON.parse(await request.text()) as { name?: string };
    const name = body.name?.trim();
    if (!name || name.length > 160) {
      return problemResponse(400, 'BAD_REQUEST', 'Invalid name', 'Choose a line name of 1–160 characters.');
    }
    const updated = { ...found, name };
    mockLineList = mockLineList.map((candidate) => candidate === found ? updated : candidate);
    const etag = bumpRepertoireVersion(repertoireId);
    persistMockState();
    return jsonResponse(updated, { etag });
  }
  if (method === 'DELETE' && lineMatch) {
    const [repertoireId, lineId] = [lineMatch[1], lineMatch[2]];
    const expectedEtag = currentRepertoireEtag(repertoireId);
    if (expectedEtag === null) {
      return problemResponse(404, 'RESOURCE_NOT_FOUND', 'Not found', 'Repertoire not found');
    }
    const found = mockLineList.find(
      (candidate) => candidate.repertoireId === repertoireId && candidate.lineId === lineId,
    );
    if (!found) return problemResponse(404, 'RESOURCE_NOT_FOUND', 'Not found', 'Line not found');
    if (ifMatch !== expectedEtag) {
      return problemResponse(
        412,
        'VERSION_MISMATCH',
        'Stale write',
        'If-Match does not match the current repertoire version.',
      );
    }
    mockLineList = mockLineList.filter(
      (candidate) =>
        candidate.repertoireId !== repertoireId || candidate.lineId !== lineId,
    );
    const nextEtag = bumpRepertoireVersion(repertoireId);
    return jsonResponse({
      detachedEdges: 0,
      disabledEdges: 0,
      retainedSharedEdges: 0,
      repertoireVersion: nextEtag.replace(/\D/g, ''),
    });
  }
  if (method === 'GET' && nodeMatch) {
    const [, repertoireId, hash, positionId] = nodeMatch;
    const target = mockRepertoireList.find(
      (candidate) => candidate.repertoireId === repertoireId,
    );
    if (!target) {
      return problemResponse(404, 'RESOURCE_NOT_FOUND', 'Not found', 'Repertoire not found');
    }
    const position = mockPositionsById.get(positionId);
    if (!position || position.zobristHash !== hash) {
      return problemResponse(404, 'RESOURCE_NOT_FOUND', 'Not found', 'Node not found');
    }
    const fixtureInfo = fixturePositionIds.has(positionId) ? nodeInfo(positionId) : null;
    const repertoireEdges = mockEdgesByRepertoire.get(repertoireId) ?? [];
    return jsonResponse(
      {
        position,
        // Overlay edges are repertoire-private even though canonical positions
        // and their server-supplied legal transitions are shared.
        outgoing: repertoireEdges.filter(
          (edge) => edge.enabled && edge.transition.from.positionId === positionId,
        ),
        legalMoves: legalUciMoves(position.canonicalFen),
        incomingPathCount:
          fixtureInfo?.incomingPathCount ??
          repertoireEdges.filter(
            (edge) => edge.enabled && edge.transition.to.positionId === positionId,
          ).length,
        minPly: fixtureInfo?.minPly ?? 0,
        maxPly: fixtureInfo?.maxPly ?? 0,
      },
      {
        etag:
          repertoireId === repertoire.repertoireId ? currentEtag() : repertoireEtag(target),
      },
    );
  }
  if (reviewMatch) {
    return handleReview(request, url, reviewMatch[1], reviewMatch[2] ?? '');
  }
  if (method === 'POST' && movesMatch) {
    return addMockMove(movesMatch[1], ifMatch, await request.text());
  }
  if (method === 'PATCH' && edgeMatch) {
    const rawBody = await request.text();
    return updateEdge(edgeMatch[1], edgeMatch[2], ifMatch, rawBody || '{}');
  }
  if (method === 'DELETE' && edgeMatch) {
    const edge = mockEdgesByRepertoire
      .get(edgeMatch[1])
      ?.find((candidate) => candidate.edgeId === edgeMatch[2]);
    if (!edge) return problemResponse(404, 'RESOURCE_NOT_FOUND', 'Not found', 'Edge not found');
    if (ifMatch !== currentRepertoireEtag(edgeMatch[1])) {
      return problemResponse(
        412,
        'VERSION_MISMATCH',
        'Stale write',
        'If-Match does not match the current repertoire version.',
      );
    }
    edge.enabled = false;
    bumpRepertoireVersion(edgeMatch[1]);
    return new Response(null, { status: 204 });
  }
  if (method === 'POST' && linesMatch) {
    const repertoireId = linesMatch[1];
    const expectedEtag = currentRepertoireEtag(repertoireId);
    if (expectedEtag === null) {
      return problemResponse(404, 'RESOURCE_NOT_FOUND', 'Not found', 'Repertoire not found');
    }
    if (ifMatch !== expectedEtag) {
      return problemResponse(
        412,
        'VERSION_MISMATCH',
        'Stale write',
        'If-Match does not match the current repertoire version.',
      );
    }
    const rawBody = await request.text();
    const body = JSON.parse(rawBody || '{}') as LineCreate;
    const created = buildMockLine(repertoireId, body);
    if (!created) {
      return problemResponse(
        422,
        'ILLEGAL_MOVE',
        'Line cannot be built',
        'The requested move order is not present in this repertoire.',
      );
    }
    mockLineList = [created, ...mockLineList];
    const nextEtag = bumpRepertoireVersion(repertoireId);
    return jsonResponse(
      {
        line: created,
        merge: {
          createdNodes: 0,
          mergedExistingNodes: 0,
          createdTransitions: 0,
          transpositionPlies: [],
          repetitionTruncated: false,
          repetitionAtPly: null,
        },
        repertoireVersion: nextEtag.replace(/\D/g, ''),
      },
      { status: 201, etag: nextEtag },
    );
  }
  if (method === 'POST' && pathname === '/imports') {
    return jsonResponse(startImportJob(), { status: 202 });
  }
  if (method === 'GET' && pathname === '/provider-accounts') {
    return jsonResponse(mockAccountList);
  }
  if (method === 'POST' && pathname === '/provider-accounts') {
    const create = JSON.parse(await request.text()) as {
      provider: 'lichess' | 'chesscom';
      username: string;
    };
    const username = create.username.toLowerCase();
    // Mirrors the server: linking the same username twice returns the existing link
    // rather than creating a second one or resetting its watermark.
    const existing = mockAccountList.find(
      (account) => account.provider === create.provider && account.username === username,
    );
    if (existing) return jsonResponse(existing, { status: 201 });
    const created = {
      providerAccountId: nextMockAccountId(),
      provider: create.provider,
      username,
      status: 'active' as const,
      connectionId: null,
      lastSyncAt: null,
      syncCursorAt: null,
      lastErrorCode: null,
      gamesSynced: '0',
      createdAt: new Date().toISOString(),
    };
    mockAccountList = [...mockAccountList, created];
    return jsonResponse(created, { status: 201 });
  }
  if (method === 'DELETE' && providerAccountMatch) {
    mockAccountList = mockAccountList.filter(
      (account) => account.providerAccountId !== providerAccountMatch[1],
    );
    return new Response(null, { status: 204 });
  }
  if (method === 'POST' && providerSyncMatch) {
    return jsonResponse(startProviderSyncJob(), { status: 202 });
  }
  if (method === 'POST' && pathname === '/evaluations') {
    return jsonResponse(mockEvaluation, { status: 200 });
  }
  if (method === 'GET' && evaluationMatch) {
    return jsonResponse(mockEvaluation);
  }
  if (method === 'GET' && jobMatch) {
    return jsonResponse(advanceJob());
  }
  if (method === 'GET' && jobEventsMatch) {
    return sseStream(jobEventsMatch[1]);
  }
  if (method === 'GET' && pathname === '/games/explorer') {
    if (url.searchParams.has('side') && mockAccountList.length === 0) return jsonResponse([]);
    const positionId = mockPositionsByState.get(positionKey(url.searchParams.get('fen') ?? ''))?.positionId;
    if (positionId === repertoire.defaultRoot.position.positionId) {
      return jsonResponse(mockExplorerMoves);
    }
    if (positionId === '2') return jsonResponse([
      { san: 'e5', uci: 'e7e5', games: 18, whiteWins: 8, draws: 4, blackWins: 6 },
      { san: 'c5', uci: 'c7c5', games: 14, whiteWins: 6, draws: 2, blackWins: 6 },
      { san: 'c6', uci: 'c7c6', games: 10, whiteWins: 3, draws: 2, blackWins: 5 },
    ]);
    return jsonResponse([]);
  }
  if (method === 'GET' && pathname === '/games') {
    return jsonResponse({ items: mockGames, nextCursor: null });
  }
  if (method === 'GET' && pathname === '/games/repertoire-matches') {
    const ids = url.searchParams.getAll('gameIds');
    if (ids.length === 0 || ids.length > 50) {
      return problemResponse(400, 'BAD_REQUEST', 'Malformed request', 'Ask for between 1 and 50 games.');
    }
    const graphs = repertoireGraphs();
    return jsonResponse(ids
      .map((id) => mockGamesById.get(id))
      .filter((game): game is Game => game !== undefined)
      .map((game) => mockMatch(game, graphs, analysisContext)));
  }
  if (method === 'GET' && gameMatch) {
    const game = mockGamesById.get(gameMatch[1]);
    if (!game) return problemResponse(404, 'RESOURCE_NOT_FOUND', 'Not found', 'Game not found');
    return jsonResponse(game);
  }
  if (method === 'POST' && deviationRunCreateMatch) {
    const body = JSON.parse((await request.text()) || '{}') as { repertoireId?: string };
    const runId = createMockRun(deviationRunCreateMatch[1], body.repertoireId ?? '');
    if (!runId) return problemResponse(404, 'RESOURCE_NOT_FOUND', 'Not found', 'Game or repertoire not found');
    return jsonResponse(startDeviationJob(runId), { status: 202 });
  }
  if (method === 'GET' && deviationRunMatch) {
    const run = mockRuns.get(deviationRunMatch[1]);
    if (!run) return problemResponse(404, 'RESOURCE_NOT_FOUND', 'Not found', 'Run not found');
    return jsonResponse(run.run);
  }
  if (method === 'GET' && deviationEventsMatch) {
    const run = mockRuns.get(deviationEventsMatch[1]);
    return jsonResponse({ items: run?.events ?? [], nextCursor: null });
  }

  return problemResponse(
    404,
    'RESOURCE_NOT_FOUND',
    'Not found',
    `No mock route for ${method} ${pathname}`,
  );
}

/** Convenience re-export for the DAG explorer view. */
export { edgeByKey, lines, outgoingFor };
